import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { flushSync } from 'react-dom'
import { toast, Toaster } from 'sonner'
import { ChatLayoutProvider, useChatLayout } from './context/ChatLayoutContext.jsx'
import { FormProvider } from './context/FormContext.jsx'
import { OrderStatusProvider } from './context/OrderStatusContext.jsx'
import {
  IntelligentWebProvider,
  useIntelligentWeb,
} from './context/IntelligentWebContext.jsx'
import { LayoutManager } from './components/widget/LayoutManager.jsx'
import { FloatingInlineMessage } from './components/widget/FloatingInlineMessage.jsx'
import { useMessageHandling } from './hooks/useMessageHandling.js'
import { useAbandonedCart } from './hooks/useAbandonedCart.js'
import { createOchatbot } from './lib/ochatbotFacade.js'
import { generateUuid } from './lib/uuid.js'
import { widgetChatDebug } from './lib/widgetChatDebug.js'
import {
  loadMessageBuffer,
  mergeRemoteMessageBuffer,
  subscribeMessageBuffer,
} from './lib/messageBufferSync.js'
import { resolveSuggestionsLayoutKey } from './templates/index.js'
import {
  applyCitationAffordances,
  engagementActionButtonsToStripSuggestions,
  enrichEngagementLeadFormStripItems,
  orderStripSuggestionItems,
  resolveStripDynamicSuggestions,
} from './lib/chatUtils.js'
import { resolveButtonEmbeddingStripSuggestions } from './lib/buttonEmbeddingSuggestions.js'
import { useWidgetAiTriggers } from './hooks/useWidgetAiTriggers.js'
import { submitWidgetLeadFormDefault } from './lib/widgetLeadFormSubmit.js'
import { isMenuTriggerPayload } from './lib/menuItemActions.js'
import {
  buildLaunchWhisperPayload,
  clearConversationCaches,
  isLaunchWhisperMarkedSent,
  launchWhisperStorageKey,
  markLaunchWhisperSent,
  shouldResendLaunchWhisperAfterThreadCleared,
  shouldSendLaunchWhisperOnPanelOpen,
  shouldShowLaunchLoadingIndicator,
} from './lib/launchConversation.js'

/**
 * Syncs `window.OmetricsAI_API` open/close with the same `open` state
 * layouts read from `ChatLayoutContext` (preview calls `api.open()`
 * before the visitor clicks the bubble).
 */
function ImperativeWidgetApi({
  onReady,
  say,
  whisper,
  writeToMessages,
  setMessages,
  ochatbot,
  restartConversation,
}) {
  const { openChat, closeChat } = useChatLayout()
  useLayoutEffect(() => {
    const api = {
      open: openChat,
      close: closeChat,
      hide: closeChat,
      show: openChat,
      say,
      whisper,
      sayAsBot: (text) =>
        writeToMessages({
          from: 'chatbot',
          type: 'text',
          text,
          time: new Date().toJSON(),
          id: generateUuid(),
        }),
      reset: () => setMessages([]),
      restartConversation,
      destroy: () => ochatbot.abort(),
    }
    onReady?.(api)
    return () => {
      ochatbot.abort()
    }
  }, [
    onReady,
    openChat,
    closeChat,
    say,
    whisper,
    writeToMessages,
    setMessages,
    ochatbot,
    restartConversation,
  ])
  return null
}

/**
 * v1 useTriggerSystem: send launch whisper on page load when targeting matches,
 * so the thread is warm before the user opens the panel (independent of trigger timing).
 */
function PageTargetingWhisperEffect({ conf, whisper }) {
  const { open } = useChatLayout()

  useLayoutEffect(() => {
    if (open || conf?.previewMode) return

    const agentId = conf?.agentId || conf?.chatId
    const launchQuestion = conf?.launchConversationId
    if (!agentId || launchQuestion == null || launchQuestion === '' || !conf?.chatServer) {
      return
    }

    const key = launchWhisperStorageKey(conf, agentId, launchQuestion)
    try {
      if (localStorage.getItem(key) === 'sent') return
    } catch {
      // continue
    }

    const launchId =
      launchQuestion === '-1' || launchQuestion === -1 ? '' : String(launchQuestion)
    const whisperData = `${agentId}ochatbot-launch${launchId}`

    try {
      whisper(whisperData)
      markLaunchWhisperSent(conf, agentId, launchQuestion)
    } catch {
      // ignore
    }
  }, [conf, open, whisper])

  return null
}

/**
 * When the panel opens on an empty thread, send the same launch whisper
 * legacy v1 uses so BotMan runs the configured launch conversation.
 */
function LaunchConversationEffect({ conf, messageCount, whisper }) {
  const { open } = useChatLayout()
  const forcedLaunchSentThisOpenRef = useRef(false)

  useLayoutEffect(() => {
    if (!open) {
      forcedLaunchSentThisOpenRef.current = false
      return
    }

    const agentId = conf?.agentId || conf?.chatId
    const launchQuestion = conf?.launchConversationId

    if (
      !shouldSendLaunchWhisperOnPanelOpen({
        messageCount,
        conf,
        agentId,
        launchQuestion,
      })
    ) {
      return
    }

    // forceWidgetOpenedWhisper bypasses the localStorage guard; still send
    // at most once per open so re-renders do not duplicate the whisper.
    if (conf?.forceWidgetOpenedWhisper && forcedLaunchSentThisOpenRef.current) {
      return
    }

    const payload = buildLaunchWhisperPayload(conf)
    if (!payload) return

    whisper(payload)
    markLaunchWhisperSent(conf, agentId, launchQuestion)
    if (conf?.forceWidgetOpenedWhisper) {
      forcedLaunchSentThisOpenRef.current = true
    }
  }, [open, messageCount, conf, whisper])

  return null
}

/**
 * After the in-memory thread was cleared (reset, cross-instance sync, etc.)
 * the launch whisper key may still be "sent", which blocks
 * LaunchConversationEffect. When the visitor manually opens a blank panel
 * and we are not waiting on a prior request, resend the launch whisper once
 * per open.
 */
function BlankThreadLaunchRecoveryEffect({
  conf,
  messageCount,
  isTyping,
  threadWasCleared,
  whisper,
}) {
  const { open } = useChatLayout()
  const recoveryAttemptedThisOpenRef = useRef(false)

  useEffect(() => {
    if (!open) {
      recoveryAttemptedThisOpenRef.current = false
      return
    }
    if (recoveryAttemptedThisOpenRef.current) return

    const agentId = conf?.agentId || conf?.chatId
    const launchQuestion = conf?.launchConversationId
    const payload = buildLaunchWhisperPayload(conf)
    const shouldResend = shouldResendLaunchWhisperAfterThreadCleared({
      open,
      messageCount,
      threadWasCleared,
      isTyping,
      launchWhisperMarkedSent:
        agentId && launchQuestion != null && launchQuestion !== ''
          ? isLaunchWhisperMarkedSent(conf, agentId, launchQuestion)
          : false,
      sendWidgetOpenedWhisper: conf?.sendWidgetOpenedWhisper,
      hasLaunchPayload: Boolean(payload),
    })

    if (!shouldResend) return

    recoveryAttemptedThisOpenRef.current = true
    widgetChatDebug('BlankThreadLaunchRecovery:resend', {
      agentId,
      launchQuestion,
    })
    whisper(payload)
    if (agentId && launchQuestion != null && launchQuestion !== '') {
      markLaunchWhisperSent(conf, agentId, launchQuestion)
    }
  }, [open, messageCount, threadWasCleared, isTyping, conf, whisper])

  return null
}

/**
 * Top-level widget app. Owns the message buffer, the ochatbot facade,
 * and wires both into the layout manager. Multi-instance support comes
 * from `widget.js`, which mounts one `<App />` per shadow host - this
 * component never reads `window.OmetricsAI_<id>` directly.
 *
 * Imperative API (`open`, `close`, `whisper`, `say`) is exposed via
 * `props.onReady`, which `widget.js` uses to publish on
 * `window.OmetricsAI_API[instanceId]` for backwards compatibility with
 * customer integrations that drive the widget from page scripts.
 */
function ConnectedChatSession({ container, panelContainer, onReady, onSubmitForm }) {
  const { conf, apiBaseUrl, instanceId, setActiveLeadForms } = useIntelligentWeb()
  const { setSuggestions, openChat, open } = useChatLayout()
  useWidgetAiTriggers(conf)
  const [messages, setMessages] = useState(() => loadInitialMessages(conf))
  const [isTyping, setIsTyping] = useState(false)
  const [launchInFlight, setLaunchInFlight] = useState(false)
  const [threadWasCleared, setThreadWasCleared] = useState(false)
  const prevMessageCountRef = useRef(null)
  const skipClearTrackingRef = useRef(false)

  const displayIsTyping =
    isTyping ||
    shouldShowLaunchLoadingIndicator({
      open,
      messageCount: messages.length,
      conf,
      isTyping,
      threadWasCleared,
      launchInFlight,
    })

  useEffect(() => {
    const prev = prevMessageCountRef.current
    prevMessageCountRef.current = messages.length
    if (messages.length > 0) {
      setThreadWasCleared(false)
      return
    }
    if (skipClearTrackingRef.current) return
    if (prev != null && prev > 0) {
      setThreadWasCleared(true)
      widgetChatDebug('thread:cleared', { previousCount: prev })
    }
  }, [messages.length])
  const [ochatbot] = useState(() =>
    createOchatbot({
      streamingEndpoint: conf?.streamingEndpoint,
      fallbackEndpoint: conf?.fallbackEndpoint,
      chatServer: conf?.chatServer,
      streamingEnabled: conf?.streamingEnabled !== false,
      sessionId: conf?.sessionId || generateUuid(),
      agentId: conf?.agentId || conf?.chatId,
      masterKey: conf?.masterKey,
      isWebAi: conf?.isWebAi,
      currentPage:
        typeof window !== 'undefined' ? window.location.href : '',
    }),
  )

  useEffect(() => {
    ochatbot.transport.configure({
      streamingEndpoint: conf?.streamingEndpoint,
      fallbackEndpoint: conf?.fallbackEndpoint,
      chatServer: conf?.chatServer,
      streamingEnabled: conf?.streamingEnabled !== false,
      agentId: conf?.agentId || conf?.chatId,
      masterKey: conf?.masterKey,
      isWebAi: Boolean(conf?.isWebAi),
    })
    ochatbot.setTypingIndicatorHandler(setIsTyping)
  }, [conf, ochatbot])

  const clearLaunchInFlight = useCallback(() => {
    setLaunchInFlight(false)
  }, [])

  useEffect(() => {
    if (
      messages.some(
        (m) => m?.from === 'chatbot' && m?.type !== 'typing_indicator',
      )
    ) {
      clearLaunchInFlight()
    }
  }, [messages, clearLaunchInFlight])

  useEffect(() => {
    const offEnd = ochatbot.onStreamEnd(() => clearLaunchInFlight())
    const offErr = ochatbot.onError(() => clearLaunchInFlight())
    return () => {
      offEnd()
      offErr()
    }
  }, [ochatbot, clearLaunchInFlight])

  const suggestionLayoutKey = useMemo(
    () => resolveSuggestionsLayoutKey(conf),
    [conf],
  )

  const { writeToMessages, persistMessages } = useMessageHandling({
    conf,
    setMessages,
    layoutType: suggestionLayoutKey,
    onSuggestionsUpdate: setSuggestions,
    instanceId,
  })

  useEffect(() => {
    const chatId = conf?.chatId
    if (!chatId) return undefined

    const applyRemoteBuffer = (remoteMessages) => {
      let changed = false
      setMessages((prev) => {
        const next = mergeRemoteMessageBuffer(prev, remoteMessages)
        if (next !== prev) changed = true
        return next
      })
      if (changed) {
        const dynamic = resolveStripDynamicSuggestions(remoteMessages, [], conf)
        if (dynamic.length > 0) setSuggestions(dynamic)
      }
    }

    return subscribeMessageBuffer(chatId, applyRemoteBuffer, {
      ignoreSourceInstanceId: instanceId,
    })
  }, [conf?.chatId, instanceId, setSuggestions])

  useEffect(() => {
    if (!open || !conf?.chatId) return
    const stored = loadMessageBuffer(conf.chatId)
    if (!stored.length) return
    let changed = false
    setMessages((prev) => {
      const next = mergeRemoteMessageBuffer(prev, stored)
      if (next !== prev) changed = true
      return next
    })
    if (changed) {
      const dynamic = resolveStripDynamicSuggestions(stored, [], conf)
      if (dynamic.length > 0) setSuggestions(dynamic)
    }
  }, [open, conf?.chatId, setSuggestions])

  const streamingMessageIdRef = useRef(null)
  const messagesRef = useRef(messages)
  const embeddingLookupCacheRef = useRef(new Map())
  useEffect(() => {
    messagesRef.current = messages
  }, [messages])

  useEffect(() => {
    const onPageHide = () => {
      persistMessages(messagesRef.current)
    }
    window.addEventListener('pagehide', onPageHide)
    return () => window.removeEventListener('pagehide', onPageHide)
  }, [persistMessages])

  useEffect(() => {
    const offToken = ochatbot.onToken(({ delta, messageId }) => {
      streamingMessageIdRef.current = messageId
      const existingIdx = messagesRef.current.findIndex((m) => m.id === messageId)
      if (existingIdx === -1) {
        setSuggestions([])
        widgetChatDebug('App:onToken:new_streaming_bubble', { messageId })
      }
      setMessages((prev) => {
        const idx = prev.findIndex((m) => m.id === messageId)
        widgetChatDebug('App:onToken', {
          messageId,
          deltaLen: typeof delta === 'string' ? delta.length : 0,
          prevLen: prev.length,
          foundIdx: idx,
        })
        if (idx === -1) {
          return [
            ...prev,
            {
              id: messageId,
              from: 'chatbot',
              type: 'text',
              text: delta,
              time: new Date().toJSON(),
              streaming: true,
              streamMarkdown: true,
            },
          ]
        }
        const next = prev.slice()
        const target = next[idx]
        next[idx] = {
          ...target,
          text: (target.text || '') + delta,
          streaming: true,
          streamMarkdown: true,
        }
        return next
      })
    })
    return offToken
  }, [ochatbot, setSuggestions])

  useEffect(() => {
    return ochatbot.onStreamEnd(({ messageId }) => {
      widgetChatDebug('App:onStreamEnd', { messageId })
      if (messageId) {
        setMessages((prev) => {
          const next = prev.map((m) =>
            m.id === messageId ? { ...m, streaming: false } : m,
          )
          persistMessages(next)
          return next
        })
      }
      streamingMessageIdRef.current = null
    })
  }, [ochatbot, persistMessages])

  useEffect(() => {
    return ochatbot.onSuggestedPrompts(({ messageId, prompts, language }) => {
      const id = messageId || streamingMessageIdRef.current
      const normalized = Array.isArray(prompts)
        ? prompts.map((p) => String(p ?? '').trim()).filter(Boolean)
        : []
      if (!id || (normalized.length === 0 && !language)) {
        widgetChatDebug('App:onSuggestedPrompts:skip', {
          id,
          promptsLen: normalized.length,
          language,
        })
        return
      }
      widgetChatDebug('App:onSuggestedPrompts:apply', {
        messageId: id,
        promptsLen: normalized.length,
        language,
      })
      setMessages((prev) => {
        const next = prev.map((m) =>
          m.id === id
            ? {
                ...m,
                ...(normalized.length > 0 ? { suggestedPrompts: normalized } : {}),
                ...(language ? { language: String(language) } : {}),
                streaming: false,
              }
            : m,
        )
        persistMessages(next)
        return next
      })
    })
  }, [ochatbot, persistMessages])

  useEffect(() => {
    return ochatbot.onCitations(({ messageId, sources }) => {
      const id = messageId || streamingMessageIdRef.current
      if (!id || !Array.isArray(sources) || sources.length === 0) {
        widgetChatDebug('App:onCitations:skip', {
          id,
          sourcesIsArray: Array.isArray(sources),
          sourcesLen: Array.isArray(sources) ? sources.length : 0,
        })
        return
      }
      const { suggestions: suggestionItems, buttons } = applyCitationAffordances(
        sources,
        conf,
        { includeReturnToMenu: true },
      )
      widgetChatDebug('App:onCitations:apply', {
        messageId: id,
        sourcesLen: sources.length,
        suggestionItemsLen: suggestionItems.length,
        buttonsLen: buttons.length,
      })
      setSuggestions(suggestionItems)
      setMessages((prev) => {
        const next = prev.map((m) =>
          m.id === id
            ? {
                ...m,
                citations: sources,
                suggestions: suggestionItems,
                buttons,
                streaming: false,
              }
            : m,
        )
        persistMessages(next)
        return next
      })
    })
  }, [ochatbot, setSuggestions, persistMessages, conf])

  useEffect(() => {
    ochatbot.setMessageHandler((msg) => {
      const streamId = streamingMessageIdRef.current
      widgetChatDebug('App:setMessageHandler', {
        type: msg?.type,
        msgId: msg?.id,
        streamId,
        textLen: typeof msg?.text === 'string' ? msg.text.length : 0,
      })
      if (msg.type === 'text' && streamId && !msg.id) {
        let merged = false
        flushSync(() => {
          setMessages((prev) => {
            const idx = prev.findIndex((m) => m.id === streamId && m.streaming)
            if (idx === -1) {
              return prev
            }
            merged = true
            const next = prev.slice()
            const target = next[idx]
            next[idx] = {
              ...target,
              ...msg,
              id: target.id,
              streaming: false,
              streamMarkdown: target.streamMarkdown === true,
            }
            persistMessages(next)
            return next
          })
        })
        widgetChatDebug('App:textMergeNoId', { streamId, merged })
        if (merged) {
          streamingMessageIdRef.current = null
        } else {
          writeToMessages({ ...msg, from: msg.from || 'chatbot' })
        }
        return
      }
      const hasInlineCitationHints =
        Object.prototype.hasOwnProperty.call(msg, 'citationHints') &&
        Array.isArray(msg.citationHints) &&
        msg.citationHints.length > 0

      let stamped = { ...msg, from: msg.from || 'chatbot' }
      if (
        Array.isArray(stamped.suggestedPrompts) &&
        stamped.suggestedPrompts.length > 0
      ) {
        stamped = {
          ...stamped,
          suggestedPrompts: stamped.suggestedPrompts
            .map((p) => String(p ?? '').trim())
            .filter(Boolean),
        }
      }
      const existingBotButtons = Array.isArray(stamped.buttons)
        ? stamped.buttons
        : []
      if (hasInlineCitationHints) {
        const { suggestions: suggestionItems, buttons: citationButtons } =
          applyCitationAffordances(msg.citationHints, conf)
        if (suggestionItems.length > 0) setSuggestions(suggestionItems)
        stamped = {
          ...stamped,
          citations: msg.citationHints,
          citationHints: msg.citationHints,
          ...(suggestionItems.length > 0
            ? { suggestions: suggestionItems }
            : {}),
          // Prelude bot templates keep their full button row; hints only feed the strip.
          ...(citationButtons.length > 0 && existingBotButtons.length === 0
            ? { buttons: citationButtons }
            : {}),
        }
      }

      writeToMessages(stamped)

      // Engagement lead-form / order-status buttons → strip cards; web_url buttons
      // may also resolve to page/product cards via embeddings lookup.
      if (
        !hasInlineCitationHints &&
        stamped?.from !== 'visitor' &&
        existingBotButtons.length > 0
      ) {
        const agentId = conf?.agentId || conf?.chatId
        const apiBaseUrl = conf?.apiBaseUrl
        const masterKey = conf?.masterKey
        const messageId = stamped.id
        const adminLeadForms = conf?.activeLeadForms || conf?.leadForms || []
        const engagementStrip = engagementActionButtonsToStripSuggestions(
          existingBotButtons,
          conf,
          adminLeadForms,
        )
        const existingSuggestions = Array.isArray(stamped.suggestions)
          ? stamped.suggestions
          : []
        const baseSuggestions =
          engagementStrip.length > 0
            ? orderStripSuggestionItems([
                ...existingSuggestions,
                ...engagementStrip,
              ])
            : existingSuggestions

        if (engagementStrip.length > 0) {
          setSuggestions(baseSuggestions)
          setMessages((prev) => {
            const next = prev.map((m) =>
              m.id === messageId
                ? {
                    ...m,
                    suggestions: baseSuggestions,
                  }
                : m,
            )
            persistMessages(next)
            return next
          })
        }

        void (async () => {
          let suggestionItems = baseSuggestions

          const enrichedLeadForms = await enrichEngagementLeadFormStripItems(
            suggestionItems,
            {
              apiBaseUrl,
              agentId,
              masterKey,
              onFormsResolved: (forms) => {
                setActiveLeadForms?.((prev) => {
                  const arr = Array.isArray(prev) ? prev : []
                  const merged = [...arr]
                  for (const form of forms) {
                    if (
                      !merged.some((f) => String(f.id) === String(form.id))
                    ) {
                      merged.push(form)
                    }
                  }
                  return merged
                })
              },
            },
          )

          if (enrichedLeadForms !== suggestionItems) {
            suggestionItems = enrichedLeadForms
            setSuggestions(suggestionItems)
            setMessages((prev) => {
              const next = prev.map((m) =>
                m.id === messageId
                  ? {
                      ...m,
                      suggestions: suggestionItems,
                    }
                  : m,
              )
              persistMessages(next)
              return next
            })
          }

          const embeddingItems = await resolveButtonEmbeddingStripSuggestions({
            buttons: existingBotButtons,
            conf,
            existingSuggestions: suggestionItems,
            apiBaseUrl,
            agentId,
            masterKey,
            lookupCache: embeddingLookupCacheRef.current,
          })
          if (!embeddingItems.length) return

          suggestionItems = [...suggestionItems, ...embeddingItems]
          setSuggestions(suggestionItems)
          setMessages((prev) => {
            const next = prev.map((m) =>
              m.id === messageId
                ? {
                    ...m,
                    suggestions: suggestionItems,
                  }
                : m,
            )
            persistMessages(next)
            return next
          })
        })()
      }
    })
  }, [ochatbot, writeToMessages, conf, setSuggestions, persistMessages, setActiveLeadForms])

  const send = useCallback(
    (text) => {
      if (!text || !text.trim()) return
      const visitorMessage = {
        id: generateUuid(),
        from: 'visitor',
        type: 'text',
        text,
        time: new Date().toJSON(),
      }
      writeToMessages(visitorMessage)
      ochatbot.callAPI(text)
    },
    [ochatbot, writeToMessages],
  )

  /** Menu prompts: visitor bubble shows label; API receives trigger payload when applicable. */
  const sendMenuAction = useCallback(
    (apiText, displayText) => {
      const api = String(apiText ?? '').trim()
      const display = String(displayText ?? apiText ?? '').trim()
      if (!api && !display) return
      const bubbleText = display || api
      writeToMessages({
        id: generateUuid(),
        from: 'visitor',
        type: 'text',
        text: bubbleText,
        time: new Date().toJSON(),
      })
      const interactive =
        isMenuTriggerPayload(api) || (api !== bubbleText && Boolean(api))
      ochatbot.callAPI(api || bubbleText, bubbleText, interactive)
    },
    [ochatbot, writeToMessages],
  )

  const say = useCallback(
    (text, showMessage = true) => {
      if (showMessage) {
        writeToMessages({
          from: 'visitor',
          type: 'text',
          text,
          time: new Date().toJSON(),
          id: generateUuid(),
        })
      }
      ochatbot.callAPI(text)
    },
    [ochatbot, writeToMessages],
  )

  const whisper = useCallback(
    (text) => {
      setLaunchInFlight(true)
      setIsTyping(true)
      say(text, false)
    },
    [say],
  )

  const agentId = conf?.agentId || conf?.chatId
  const onEventTrack = useCallback((eventName, params = {}) => {
    try {
      if (window.ometricsAPI && Array.isArray(window.ometricsAPI)) {
        window.ometricsAPI.push('o_trackEvent', eventName, params)
      }
    } catch {
      // ignore
    }
  }, [])

  const { freeShippingInlineMessage, freeShippingInlineMessageText } =
    useAbandonedCart({
      conf,
      agentId,
      openChat,
      whisper,
      onEventTrack,
    })

  const [dismissedFreeShippingText, setDismissedFreeShippingText] = useState('')
  const activeFreeShippingText = freeShippingInlineMessage
    ? String(freeShippingInlineMessageText || '').trim()
    : ''

  useEffect(() => {
    // A newer message (or the same trigger firing again) should slide back
    // in even if the visitor already dismissed a previous one.
    if (activeFreeShippingText) setDismissedFreeShippingText('')
  }, [activeFreeShippingText])

  const showFreeShippingInlineMessage =
    Boolean(activeFreeShippingText) &&
    activeFreeShippingText !== dismissedFreeShippingText &&
    !open &&
    String(conf?.kind || '').toLowerCase() !== 'injected'

  const handleLeadFormSubmit = useCallback(
    async (payload) => {
      if (typeof onSubmitForm === 'function') {
        await onSubmitForm(payload)
        return
      }
      try {
        await submitWidgetLeadFormDefault(payload, { apiBaseUrl, conf })
      } catch (e) {
        console.error(e)
        toast.error(
          e instanceof Error ? e.message : 'Could not submit the form.',
        )
        throw e
      }
    },
    [onSubmitForm, apiBaseUrl, conf],
  )

  const handleOrderStatusResult = useCallback(
    ({ messageHtml, buttons }) => {
      writeToMessages({
        id: generateUuid(),
        from: 'chatbot',
        type: 'buttons',
        text: messageHtml,
        buttons: Array.isArray(buttons) ? buttons : [],
        time: new Date().toJSON(),
      })
    },
    [writeToMessages],
  )

  const restartConversation = useCallback(() => {
    skipClearTrackingRef.current = true
    setThreadWasCleared(false)
    setMessages([])
    setSuggestions([])
    clearConversationCaches(conf)
    const payload = buildLaunchWhisperPayload(conf)
    if (payload) {
      whisper(payload)
      const agentId = conf?.agentId || conf?.chatId
      const launchQuestion = conf?.launchConversationId
      if (agentId && launchQuestion != null && launchQuestion !== '') {
        markLaunchWhisperSent(conf, agentId, launchQuestion)
      }
    }
    skipClearTrackingRef.current = false
  }, [conf, ochatbot, whisper, setSuggestions])

  return (
    <>
      <ImperativeWidgetApi
        onReady={onReady}
        say={say}
        whisper={whisper}
        writeToMessages={writeToMessages}
        setMessages={setMessages}
        ochatbot={ochatbot}
        restartConversation={restartConversation}
      />
      <PageTargetingWhisperEffect conf={conf} whisper={whisper} />
      <LaunchConversationEffect
        conf={conf}
        messageCount={messages.length}
        whisper={whisper}
      />
      <BlankThreadLaunchRecoveryEffect
        conf={conf}
        messageCount={messages.length}
        isTyping={isTyping}
        threadWasCleared={threadWasCleared}
        whisper={whisper}
      />
      <LayoutManager
        conf={conf}
        messages={messages}
        isTyping={displayIsTyping}
        onSend={send}
        onSendMenuAction={sendMenuAction}
        ochatbot={ochatbot}
        container={container}
        panelContainer={panelContainer}
        onLeadFormSubmit={handleLeadFormSubmit}
        onOrderStatusResult={handleOrderStatusResult}
        onRestartConversation={restartConversation}
      />
      {showFreeShippingInlineMessage && (
        <FloatingInlineMessage
          conf={conf}
          text={activeFreeShippingText}
          onOpen={openChat}
          onDismiss={() => setDismissedFreeShippingText(activeFreeShippingText)}
        />
      )}
      <Toaster
        position="top-right"
        theme="light"
        duration={4000}
        richColors
        closeButton
        containerAriaLabel="Chat notifications"
      />
    </>
  )
}

function AppShell({ container, panelContainer, onReady, onSubmitForm }) {
  const { conf } = useIntelligentWeb()
  const initialPanelOpen = Boolean(
    conf?.startOpen ||
      conf?.layout === 'open' ||
      conf?.previewMode,
  )
  return (
    <ChatLayoutProvider initialOpen={initialPanelOpen}>
      <FormProvider>
        <OrderStatusProvider>
          <ConnectedChatSession
            container={container}
            panelContainer={panelContainer}
            onReady={onReady}
            onSubmitForm={onSubmitForm}
          />
        </OrderStatusProvider>
      </FormProvider>
    </ChatLayoutProvider>
  )
}

function loadInitialMessages(conf) {
  if (!conf?.chatId) return []
  return loadMessageBuffer(conf.chatId)
}

/**
 * Default export wraps the AppShell in `IntelligentWebProvider` so
 * components inside can reach the shared `conf` via context. Callers
 * (dev harness, widget.js) pass the resolved conf in.
 */
export default function App({
  conf,
  instanceId,
  apiBaseUrl,
  container,
  panelContainer,
  onReady,
  onSubmitForm,
}) {
  const initialValue = useMemo(
    () => ({ conf, instanceId, apiBaseUrl }),
    [conf, instanceId, apiBaseUrl],
  )
  return (
    <IntelligentWebProvider value={initialValue}>
      <AppShell
        container={container}
        panelContainer={panelContainer}
        onReady={onReady}
        onSubmitForm={onSubmitForm}
      />
    </IntelligentWebProvider>
  )
}
